/**
 * triagingService.js
 * 
 * Implements rule-based claim triaging in the browser.
 * MVP fields: Estimated Loss, Loss Location (country), Line Of Business (PROPERTY),
 * Industry Classification, Scope Code, GLE flag, Business Segment.
 * 
 * Outputs a TriageResult object with assigned adjuster, handler, rules fired and audit log.
 */

/* ---------------------------
   Enums & Constants
   --------------------------- */
export const LineOfBusiness = { PROPERTY: 'PROPERTY' };

export const Region = { NA: 'NA', EMEA: 'EMEA', APAC: 'APAC', LATAM: 'LATAM' };

export const ScopeCode = { SC1: 'SC1', SC2: 'SC2', SC3: 'SC3', SC4: 'SC4' };

export const IndustryRisk = { HIGH: 'HIGH', MEDIUM: 'MEDIUM', LOW: 'LOW' };

export const AdjusterLevel = { 
  JR: 'JR', 
  EXPERIENCED: 'EXPERIENCED', 
  SENIOR: 'SENIOR', 
  CAT: 'CAT' 
};

export const BusinessSegment = { 
  SMALL: 'SMALL', 
  MID_CORP: 'MID_CORP', 
  LCR: 'LCR' 
};

export const HandlerType = {
  GENERAL_COMMERCIAL: 'GENERAL_COMMERCIAL',
  SEGMENT_SPECIFIC: 'SEGMENT_SPECIFIC',
  DEDICATED_ACCOUNT: 'DEDICATED_ACCOUNT',
  SPECIALIST_CONSTRUCTION: 'SPECIALIST_CONSTRUCTION',
  SPECIALIST_OIL_GAS: 'SPECIALIST_OIL_GAS',
  SPECIALIST_OTHER: 'SPECIALIST_OTHER'
};

/* ---------------------------
   Reference Data
   --------------------------- */

// Country -> region mapping
const countryToRegion = {
  // North America
  'US': Region.NA,
  'USA': Region.NA,
  'United States': Region.NA,
  'United States of America': Region.NA,
  'Canada': Region.NA,
  
  // Latin America
  'Mexico': Region.LATAM,
  'Brazil': Region.LATAM,
  'Argentina': Region.LATAM,
  'Chile': Region.LATAM,
  'Colombia': Region.LATAM,
  'Peru': Region.LATAM,
  'Venezuela': Region.LATAM,
  'Ecuador': Region.LATAM,
  'Bolivia': Region.LATAM,
  'Paraguay': Region.LATAM,
  'Uruguay': Region.LATAM,
  
  // Europe, Middle East, Africa
  'Germany': Region.EMEA,
  'France': Region.EMEA,
  'Ireland': Region.EMEA,
  'United Kingdom': Region.EMEA,
  'UK': Region.EMEA,
  'Great Britain': Region.EMEA,
  'England': Region.EMEA,
  'Scotland': Region.EMEA,
  'Wales': Region.EMEA,
  'Spain': Region.EMEA,
  'Italy': Region.EMEA,
  'Netherlands': Region.EMEA,
  'Belgium': Region.EMEA,
  'Switzerland': Region.EMEA,
  'Austria': Region.EMEA,
  'Portugal': Region.EMEA,
  'Sweden': Region.EMEA,
  'Norway': Region.EMEA,
  'Denmark': Region.EMEA,
  'Finland': Region.EMEA,
  'Poland': Region.EMEA,
  'Czech Republic': Region.EMEA,
  'Greece': Region.EMEA,
  'Turkey': Region.EMEA,
  'UAE': Region.EMEA,
  'United Arab Emirates': Region.EMEA,
  'Dubai': Region.EMEA,
  'Saudi Arabia': Region.EMEA,
  'Qatar': Region.EMEA,
  'Kuwait': Region.EMEA,
  'Bahrain': Region.EMEA,
  'Oman': Region.EMEA,
  'South Africa': Region.EMEA,
  'Egypt': Region.EMEA,
  'Nigeria': Region.EMEA,
  'Kenya': Region.EMEA,
  'Morocco': Region.EMEA,
  'Israel': Region.EMEA,
  'Russia': Region.EMEA,
  
  // Asia Pacific
  'India': Region.APAC,
  'China': Region.APAC,
  'Japan': Region.APAC,
  'Australia': Region.APAC,
  'Singapore': Region.APAC,
  'South Korea': Region.APAC,
  'Korea': Region.APAC,
  'Thailand': Region.APAC,
  'Malaysia': Region.APAC,
  'Indonesia': Region.APAC,
  'Philippines': Region.APAC,
  'Vietnam': Region.APAC,
  'New Zealand': Region.APAC,
  'Hong Kong': Region.APAC,
  'Taiwan': Region.APAC,
  'Pakistan': Region.APAC,
  'Bangladesh': Region.APAC,
  'Sri Lanka': Region.APAC,
  'Myanmar': Region.APAC,
  'Cambodia': Region.APAC,
  'Laos': Region.APAC,
  'Nepal': Region.APAC,
  'Brunei': Region.APAC,
  'Mongolia': Region.APAC,
  'Macau': Region.APAC
};

// Industry classification to risk mapping
const highRiskIndustries = new Set([
  'Construction', 
  'Mining', 
  'Oil/Gas', 
  'Oil & Gas',
  'Aviation', 
  'Healthcare', 
  'Maritime', 
  'Shipping'
]);

const mediumRiskIndustries = new Set([
  'Manufacturing', 
  'Transportation', 
  'Retail', 
  'Hospitality', 
  'Agriculture'
]);

/* ---------------------------
   Adjuster & Handler Pools
   --------------------------- */

export const adjusterPool = [
  // Junior Adjusters - NA
  { name: 'Michael Carter', region: Region.NA, level: AdjusterLevel.JR },
  { name: 'Emily Davis', region: Region.NA, level: AdjusterLevel.JR },
  { name: 'Ryan Mitchell', region: Region.NA, level: AdjusterLevel.JR },
  { name: 'Sarah Williams', region: Region.NA, level: AdjusterLevel.JR },
  { name: 'Kevin Anderson', region: Region.NA, level: AdjusterLevel.JR },
  
  // Junior Adjusters - EMEA
  { name: 'Sophie Müller', region: Region.EMEA, level: AdjusterLevel.JR },
  { name: 'Marco Rossi', region: Region.EMEA, level: AdjusterLevel.JR },
  { name: 'Emma Schmidt', region: Region.EMEA, level: AdjusterLevel.JR },
  { name: 'Pierre Laurent', region: Region.EMEA, level: AdjusterLevel.JR },
  { name: 'Anna Kowalski', region: Region.EMEA, level: AdjusterLevel.JR },
  
  // Junior Adjusters - APAC
  { name: 'Arun Kumar', region: Region.APAC, level: AdjusterLevel.JR },
  { name: 'Li Wei', region: Region.APAC, level: AdjusterLevel.JR },
  { name: 'Priya Sharma', region: Region.APAC, level: AdjusterLevel.JR },
  { name: 'Yuki Yamamoto', region: Region.APAC, level: AdjusterLevel.JR },
  { name: 'Chen Wang', region: Region.APAC, level: AdjusterLevel.JR },
  
  // Junior Adjusters - LATAM
  { name: 'Carlos Rodríguez', region: Region.LATAM, level: AdjusterLevel.JR },
  { name: 'Maria Santos', region: Region.LATAM, level: AdjusterLevel.JR },
  { name: 'Diego Martinez', region: Region.LATAM, level: AdjusterLevel.JR },
  { name: 'Isabella Costa', region: Region.LATAM, level: AdjusterLevel.JR },
  { name: 'Rafael Gomez', region: Region.LATAM, level: AdjusterLevel.JR },
  
  // Experienced Adjusters - NA
  { name: 'Jessica Thompson', region: Region.NA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Robert Taylor', region: Region.NA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Amanda Wilson', region: Region.NA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Christopher Moore', region: Region.NA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Nicole Brown', region: Region.NA, level: AdjusterLevel.EXPERIENCED },
  
  // Experienced Adjusters - EMEA
  { name: 'Omar El-Hassan', region: Region.EMEA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Elena Petrova', region: Region.EMEA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Hans Weber', region: Region.EMEA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Amelia Clarke', region: Region.EMEA, level: AdjusterLevel.EXPERIENCED },
  { name: 'Andreas Bergmann', region: Region.EMEA, level: AdjusterLevel.EXPERIENCED },
  
  // Experienced Adjusters - APAC
  { name: 'Mei Lin', region: Region.APAC, level: AdjusterLevel.EXPERIENCED },
  { name: 'Raj Patel', region: Region.APAC, level: AdjusterLevel.EXPERIENCED },
  { name: 'Kenji Suzuki', region: Region.APAC, level: AdjusterLevel.EXPERIENCED },
  { name: 'Siti Rahman', region: Region.APAC, level: AdjusterLevel.EXPERIENCED },
  { name: 'Zhang Yi', region: Region.APAC, level: AdjusterLevel.EXPERIENCED },
  
  // Experienced Adjusters - LATAM
  { name: 'Fernanda Silva', region: Region.LATAM, level: AdjusterLevel.EXPERIENCED },
  { name: 'Gabriel Mendez', region: Region.LATAM, level: AdjusterLevel.EXPERIENCED },
  { name: 'Adriana Lopez', region: Region.LATAM, level: AdjusterLevel.EXPERIENCED },
  { name: 'Marco Oliveira', region: Region.LATAM, level: AdjusterLevel.EXPERIENCED },
  { name: 'Carla Vargas', region: Region.LATAM, level: AdjusterLevel.EXPERIENCED },
  
  // Senior Adjusters - NA
  { name: 'David Johnson', region: Region.NA, level: AdjusterLevel.SENIOR },
  { name: 'Patricia Harris', region: Region.NA, level: AdjusterLevel.SENIOR },
  { name: 'James Robinson', region: Region.NA, level: AdjusterLevel.SENIOR },
  { name: 'Linda Martinez', region: Region.NA, level: AdjusterLevel.SENIOR },
  { name: 'William Clark', region: Region.NA, level: AdjusterLevel.SENIOR },
  
  // Senior Adjusters - EMEA
  { name: 'Claire Dubois', region: Region.EMEA, level: AdjusterLevel.SENIOR },
  { name: 'Giovanni Ferrari', region: Region.EMEA, level: AdjusterLevel.SENIOR },
  { name: 'Ingrid Hansen', region: Region.EMEA, level: AdjusterLevel.SENIOR },
  { name: 'Miguel Santos', region: Region.EMEA, level: AdjusterLevel.SENIOR },
  { name: 'Fatima Al-Rashid', region: Region.EMEA, level: AdjusterLevel.SENIOR },
  
  // Senior Adjusters - APAC
  { name: 'Hiroshi Tanaka', region: Region.APAC, level: AdjusterLevel.SENIOR },
  { name: 'Anjali Desai', region: Region.APAC, level: AdjusterLevel.SENIOR },
  { name: 'Kim Min-jun', region: Region.APAC, level: AdjusterLevel.SENIOR },
  { name: 'Tan Wei Ming', region: Region.APAC, level: AdjusterLevel.SENIOR },
  { name: 'Sakura Nakamura', region: Region.APAC, level: AdjusterLevel.SENIOR },
  
  // Senior Adjusters - LATAM
  { name: 'Juan Pérez', region: Region.LATAM, level: AdjusterLevel.SENIOR },
  { name: 'Elena Ramirez', region: Region.LATAM, level: AdjusterLevel.SENIOR },
  { name: 'Roberto Alves', region: Region.LATAM, level: AdjusterLevel.SENIOR },
  { name: 'Carmen Torres', region: Region.LATAM, level: AdjusterLevel.SENIOR },
  { name: 'Alejandro Castro', region: Region.LATAM, level: AdjusterLevel.SENIOR },
  
  // CAT Adjusters - NA
  { name: 'Ashley Miller', region: Region.NA, level: AdjusterLevel.CAT },
  { name: 'Brandon Lewis', region: Region.NA, level: AdjusterLevel.CAT },
  { name: 'Melissa Walker', region: Region.NA, level: AdjusterLevel.CAT },
  { name: 'Justin Hall', region: Region.NA, level: AdjusterLevel.CAT },
  { name: 'Stephanie Young', region: Region.NA, level: AdjusterLevel.CAT },
  
  // CAT Adjusters - EMEA
  { name: 'Liam O\'Connor', region: Region.EMEA, level: AdjusterLevel.CAT },
  { name: 'Charlotte Evans', region: Region.EMEA, level: AdjusterLevel.CAT },
  { name: 'Lars Andersson', region: Region.EMEA, level: AdjusterLevel.CAT },
  { name: 'Natasha Ivanova', region: Region.EMEA, level: AdjusterLevel.CAT },
  { name: 'Dimitri Volkov', region: Region.EMEA, level: AdjusterLevel.CAT },
  
  // CAT Adjusters - APAC
  { name: 'Samantha Lee', region: Region.APAC, level: AdjusterLevel.CAT },
  { name: 'Arjun Singh', region: Region.APAC, level: AdjusterLevel.CAT },
  { name: 'Mia Chen', region: Region.APAC, level: AdjusterLevel.CAT },
  { name: 'Ravi Krishnan', region: Region.APAC, level: AdjusterLevel.CAT },
  { name: 'Aya Fujimoto', region: Region.APAC, level: AdjusterLevel.CAT },
  
  // CAT Adjusters - LATAM
  { name: 'Valentina Torres', region: Region.LATAM, level: AdjusterLevel.CAT },
  { name: 'Eduardo Morales', region: Region.LATAM, level: AdjusterLevel.CAT },
  { name: 'Gabriela Ruiz', region: Region.LATAM, level: AdjusterLevel.CAT },
  { name: 'Luis Fernandez', region: Region.LATAM, level: AdjusterLevel.CAT },
  { name: 'Sofia Delgado', region: Region.LATAM, level: AdjusterLevel.CAT }
];

export const handlerPool = [
  // General Commercial Handlers - NA
  { name: 'Ethan Brooks', region: Region.NA, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Rachel Green', region: Region.NA, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Tyler Scott', region: Region.NA, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Monica Parker', region: Region.NA, type: HandlerType.GENERAL_COMMERCIAL },
  
  // General Commercial Handlers - EMEA
  { name: 'Clara Müller', region: Region.EMEA, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Oliver Smith', region: Region.EMEA, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Sophia Martin', region: Region.EMEA, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Thomas Bernard', region: Region.EMEA, type: HandlerType.GENERAL_COMMERCIAL },
  
  // General Commercial Handlers - APAC
  { name: 'Ravi Iyer', region: Region.APAC, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Nina Patel', region: Region.APAC, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Alex Wong', region: Region.APAC, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Jasmine Tan', region: Region.APAC, type: HandlerType.GENERAL_COMMERCIAL },
  
  // General Commercial Handlers - LATAM
  { name: 'Andrés Morales', region: Region.LATAM, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Valeria Diaz', region: Region.LATAM, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Pedro Sanchez', region: Region.LATAM, type: HandlerType.GENERAL_COMMERCIAL },
  { name: 'Daniela Cruz', region: Region.LATAM, type: HandlerType.GENERAL_COMMERCIAL },

  // Segment Specific Handlers - NA
  { name: 'Natalie Cooper', region: Region.NA, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Derek Morgan', region: Region.NA, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Chloe Bennett', region: Region.NA, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Marcus Reed', region: Region.NA, type: HandlerType.SEGMENT_SPECIFIC },
  
  // Segment Specific Handlers - EMEA
  { name: 'Omar Haddad', region: Region.EMEA, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Victoria Carlsson', region: Region.EMEA, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Mikhail Sokolov', region: Region.EMEA, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Francesca Romano', region: Region.EMEA, type: HandlerType.SEGMENT_SPECIFIC },
  
  // Segment Specific Handlers - APAC
  { name: 'Meera Kapoor', region: Region.APAC, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'David Kim', region: Region.APAC, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Lily Zhang', region: Region.APAC, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Vikram Mehta', region: Region.APAC, type: HandlerType.SEGMENT_SPECIFIC },
  
  // Segment Specific Handlers - LATAM
  { name: 'Camila Herrera', region: Region.LATAM, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Fernando Rojas', region: Region.LATAM, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Beatriz Lima', region: Region.LATAM, type: HandlerType.SEGMENT_SPECIFIC },
  { name: 'Ricardo Navarro', region: Region.LATAM, type: HandlerType.SEGMENT_SPECIFIC },

  // Dedicated Account Handlers - NA
  { name: 'Benjamin Scott', region: Region.NA, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Katherine White', region: Region.NA, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Alexander Hughes', region: Region.NA, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Victoria Nelson', region: Region.NA, type: HandlerType.DEDICATED_ACCOUNT },
  
  // Dedicated Account Handlers - EMEA
  { name: 'Lucas Bennett', region: Region.EMEA, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Isabella Fischer', region: Region.EMEA, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Sebastian Richter', region: Region.EMEA, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Olivia Thompson', region: Region.EMEA, type: HandlerType.DEDICATED_ACCOUNT },
  
  // Dedicated Account Handlers - APAC
  { name: 'Daniel Wong', region: Region.APAC, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Priyanka Shah', region: Region.APAC, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'James Lee', region: Region.APAC, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Emily Nguyen', region: Region.APAC, type: HandlerType.DEDICATED_ACCOUNT },
  
  // Dedicated Account Handlers - LATAM
  { name: 'Rodrigo Silva', region: Region.LATAM, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Mariana Cardoso', region: Region.LATAM, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Antonio Pereira', region: Region.LATAM, type: HandlerType.DEDICATED_ACCOUNT },
  { name: 'Valentina Gomez', region: Region.LATAM, type: HandlerType.DEDICATED_ACCOUNT },

  // Specialist Handlers - Construction - NA
  { name: 'Hannah Reed', region: Region.NA, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Jacob Foster', region: Region.NA, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Samantha Brooks', region: Region.NA, type: HandlerType.SPECIALIST_CONSTRUCTION },
  
  // Specialist Handlers - Construction - EMEA
  { name: 'Isabelle Dubois', region: Region.EMEA, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Henrik Johansson', region: Region.EMEA, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Marta Nowak', region: Region.EMEA, type: HandlerType.SPECIALIST_CONSTRUCTION },
  
  // Specialist Handlers - Construction - APAC
  { name: 'Sanjay Patel', region: Region.APAC, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Amy Chen', region: Region.APAC, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Rahul Gupta', region: Region.APAC, type: HandlerType.SPECIALIST_CONSTRUCTION },
  
  // Specialist Handlers - Construction - LATAM
  { name: 'Lucia Fernandez', region: Region.LATAM, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Miguel Ortiz', region: Region.LATAM, type: HandlerType.SPECIALIST_CONSTRUCTION },
  { name: 'Patricia Mendoza', region: Region.LATAM, type: HandlerType.SPECIALIST_CONSTRUCTION },

  // Specialist Handlers - Oil/Gas - NA
  { name: 'Christopher Vaughn', region: Region.NA, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Jennifer Palmer', region: Region.NA, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Matthew Bryant', region: Region.NA, type: HandlerType.SPECIALIST_OIL_GAS },
  
  // Specialist Handlers - Oil/Gas - EMEA
  { name: 'Stefan Novak', region: Region.EMEA, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Katarina Petrova', region: Region.EMEA, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Ahmed Hassan', region: Region.EMEA, type: HandlerType.SPECIALIST_OIL_GAS },
  
  // Specialist Handlers - Oil/Gas - APAC
  { name: 'Akira Mori', region: Region.APAC, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Kavita Reddy', region: Region.APAC, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Hassan Abdullah', region: Region.APAC, type: HandlerType.SPECIALIST_OIL_GAS },
  
  // Specialist Handlers - Oil/Gas - LATAM
  { name: 'Mateo Castillo', region: Region.LATAM, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Ana Reyes', region: Region.LATAM, type: HandlerType.SPECIALIST_OIL_GAS },
  { name: 'Javier Medina', region: Region.LATAM, type: HandlerType.SPECIALIST_OIL_GAS }
];

/* ---------------------------
   Utility Functions
   --------------------------- */

export function resolveRegion(country) {
  if (!country) return Region.EMEA; // default
  
  // Normalize input: trim, lowercase, remove extra spaces
  let normalized = String(country).trim().toLowerCase().replace(/\s+/g, ' ');
  
  // Handle "City, Country" format (e.g., "Singapore, Singapore" -> "singapore")
  // Split by comma and take the last part as it's usually the country
  if (normalized.includes(',')) {
    const parts = normalized.split(',').map(p => p.trim());
    // Use the last part (country) or if both parts are same, use one
    normalized = parts[parts.length - 1];
  }
  
  // Create a lowercase version of the country mapping for case-insensitive lookup
  const lowerCaseMapping = {};
  Object.keys(countryToRegion).forEach(key => {
    lowerCaseMapping[key.toLowerCase()] = countryToRegion[key];
  });
  
  // Try exact match first
  if (lowerCaseMapping[normalized]) {
    return lowerCaseMapping[normalized];
  }
  
  // Try partial match - check if any country key contains the input or vice versa
  for (const [key, region] of Object.entries(lowerCaseMapping)) {
    if (key.includes(normalized) || normalized.includes(key)) {
      return region;
    }
  }
  
  // Default to EMEA if no match found
  return Region.EMEA;
}

export function resolveIndustryRisk(industry) {
  if (!industry) return IndustryRisk.LOW;
  if (highRiskIndustries.has(industry)) return IndustryRisk.HIGH;
  if (mediumRiskIndustries.has(industry)) return IndustryRisk.MEDIUM;
  return IndustryRisk.LOW;
}

export function findAdjuster(region, level) {
  return adjusterPool.find(a => a.region === region && a.level === level)
    || adjusterPool.find(a => a.level === level) // fallback any region same level
    || null;
}

export function findHandler(region, type) {
  return handlerPool.find(h => h.region === region && h.type === type)
    || handlerPool.find(h => h.type === type)
    || null;
}

/* ---------------------------
   Core Triage Logic
   --------------------------- */

function assignAdjuster(claim, reasons) {
  // Only PROPERTY in MVP
  if (claim.lob !== LineOfBusiness.PROPERTY) {
    reasons.push('LOB not PROPERTY → no adjuster assigned in MVP');
    return null;
  }

  // 1. Catastrophe override
  // Check if GLE flag is explicitly true (boolean)
  if (claim.gleFlag === true) {
    const gleDesc = claim.gleFlagDescription ? ` (${claim.gleFlagDescription})` : '';
    reasons.push(`GLE flag true${gleDesc} → CAT adjuster override`);
    return findAdjuster(claim.region, AdjusterLevel.CAT);
  }

  // 2. Scope override (SC3/SC4 escalate to SENIOR)
  if (claim.scopeCode === ScopeCode.SC3 || claim.scopeCode === ScopeCode.SC4) {
    reasons.push(`Scope ${claim.scopeCode} → escalate to SENIOR adjuster`);
    return findAdjuster(claim.region, AdjusterLevel.SENIOR);
  }

  // 3. Baseline by estimated loss
  let level;
  if (claim.estimatedLoss < 50000) level = AdjusterLevel.JR;
  else if (claim.estimatedLoss <= 250000) level = AdjusterLevel.EXPERIENCED;
  else level = AdjusterLevel.SENIOR;
  reasons.push(`Baseline by Estimated Loss $${claim.estimatedLoss.toLocaleString()} → ${level}`);

  // 4. High-risk industry escalation (one level up)
  if (claim.industryRisk === IndustryRisk.HIGH) {
    if (level === AdjusterLevel.JR) {
      level = AdjusterLevel.EXPERIENCED;
      reasons.push('Industry HIGH risk → escalate JR → EXPERIENCED');
    } else if (level === AdjusterLevel.EXPERIENCED) {
      level = AdjusterLevel.SENIOR;
      reasons.push('Industry HIGH risk → escalate EXPERIENCED → SENIOR');
    } else {
      reasons.push('Industry HIGH risk but already SENIOR or higher → no further escalation');
    }
  }

  return findAdjuster(claim.region, level);
}

function determineBaselineHandlerType(claim, reasons) {
  const le = claim.estimatedLoss;
  const seg = claim.businessSegment;
  
  // For LE <50k and 50k-250k same mapping in the spec for Small/Mid/LCR
  if (le <= 250000) {
    if (seg === BusinessSegment.SMALL) {
      reasons.push(`Baseline handler: LE=$${le.toLocaleString()}, BusinessSegment=${seg} → GENERAL_COMMERCIAL`);
      return HandlerType.GENERAL_COMMERCIAL;
    }
    if (seg === BusinessSegment.MID_CORP) {
      reasons.push(`Baseline handler: LE=$${le.toLocaleString()}, BusinessSegment=${seg} → SEGMENT_SPECIFIC`);
      return HandlerType.SEGMENT_SPECIFIC;
    }
    if (seg === BusinessSegment.LCR) {
      reasons.push(`Baseline handler: LE=$${le.toLocaleString()}, BusinessSegment=${seg} → DEDICATED_ACCOUNT`);
      return HandlerType.DEDICATED_ACCOUNT;
    }
  } else { // > 250k
    if (seg === BusinessSegment.SMALL) {
      reasons.push(`Baseline handler: LE>$250,000, SMALL → SEGMENT_SPECIFIC`);
      return HandlerType.SEGMENT_SPECIFIC;
    }
    if (seg === BusinessSegment.MID_CORP) {
      reasons.push(`Baseline handler: LE>$250,000, MID_CORP → SEGMENT_SPECIFIC`);
      return HandlerType.SEGMENT_SPECIFIC;
    }
    if (seg === BusinessSegment.LCR) {
      reasons.push(`Baseline handler: LE>$250,000, LCR → DEDICATED_ACCOUNT`);
      return HandlerType.DEDICATED_ACCOUNT;
    }
  }
  
  reasons.push('Fallback baseline handler → GENERAL_COMMERCIAL');
  return HandlerType.GENERAL_COMMERCIAL;
}

function assignHandler(claim, reasons) {
  if (claim.lob !== LineOfBusiness.PROPERTY) {
    reasons.push('LOB not PROPERTY → no handler assigned in MVP');
    return null;
  }

  // baseline
  let handlerType = determineBaselineHandlerType(claim, reasons);

  // Scope override
  if (claim.scopeCode === ScopeCode.SC3 || claim.scopeCode === ScopeCode.SC4) {
    reasons.push(`Scope ${claim.scopeCode} → override to DEDICATED_ACCOUNT`);
    handlerType = HandlerType.DEDICATED_ACCOUNT;
  }

  // Industry override (specialists)
  const industry = (claim.industryCode || '').toLowerCase();
  if (industry.includes('construction')) {
    reasons.push('Industry Construction → override to SPECIALIST_CONSTRUCTION');
    handlerType = HandlerType.SPECIALIST_CONSTRUCTION;
  } else if (industry.includes('oil') || industry.includes('gas') || industry.includes('mining')) {
    reasons.push('Industry Oil/Gas/Mining → override to SPECIALIST_OIL_GAS');
    handlerType = HandlerType.SPECIALIST_OIL_GAS;
  } else if (claim.industryRisk === IndustryRisk.HIGH) {
    reasons.push('Industry HIGH risk → override to SPECIALIST_OTHER');
    handlerType = HandlerType.SPECIALIST_OTHER;
  }

  return findHandler(claim.region, handlerType);
}

/* ---------------------------
   Triage Orchestration
   --------------------------- */

// In-memory audit log for MVP
let auditLog = [];

export function getAuditLog() {
  return [...auditLog];
}

export function clearAuditLog() {
  auditLog = [];
}

export function triageClaim(rawClaim) {
  // Copy - do not mutate external object
  const claim = { ...rawClaim };

  // Enrichment
  claim.region = resolveRegion(claim.lossCountry);
  claim.industryRisk = resolveIndustryRisk(claim.industryCode);

  const reasons = [];
  reasons.push(`Region resolved: ${claim.region}`);
  reasons.push(`Industry risk resolved: ${claim.industryRisk}`);

  const assignedAdjuster = assignAdjuster(claim, reasons);
  const assignedHandler = assignHandler(claim, reasons);

  const triageResult = {
    claimId: claim.claimId,
    claim: claim,
    adjuster: assignedAdjuster ? { ...assignedAdjuster } : null,
    handler: assignedHandler ? { ...assignedHandler } : null,
    rulesFired: reasons,
    timestamp: new Date().toISOString()
  };

  // Log to audit (no override)
  auditLog.push({
    claimId: claim.claimId,
    action: 'AUTO_TRIAGE',
    result: triageResult,
    override: false,
    timestamp: triageResult.timestamp
  });

  return triageResult;
}

/* ---------------------------
   Human Override Helper
   --------------------------- */

export function applyOverride(claimId, overridePayload, userId, reason) {
  // Find existing audit entry for auto triage (latest)
  const entry = [...auditLog].reverse().find(e => e.claimId === claimId && e.action === 'AUTO_TRIAGE');
  if (!entry) throw new Error('No auto triage found to override for claim ' + claimId);

  const overrideEntry = {
    claimId,
    action: 'OVERRIDE',
    overriddenBy: userId,
    overrideReason: reason,
    overridePayload,
    previous: entry.result,
    timestamp: new Date().toISOString()
  };
  
  auditLog.push(overrideEntry);
  return overrideEntry;
}

/* ---------------------------
   Export for convenience
   --------------------------- */

export default {
  triageClaim,
  applyOverride,
  getAuditLog,
  clearAuditLog,
  LineOfBusiness,
  Region,
  ScopeCode,
  IndustryRisk,
  AdjusterLevel,
  BusinessSegment,
  HandlerType,
  adjusterPool,
  handlerPool,
  resolveRegion,
  resolveIndustryRisk,
  findAdjuster,
  findHandler
};
